import API from 'lib/api'
import { getMessage } from 'lib/translator'
import _ from 'lodash'

const getAddressTypeFromSalesOrder = (order) => {
  let addressType = ''
  if (order?.type) {
    switch (order.type) {
      case 'SALES_ORDER_TYPE_BUSINESS_TO_BUSINESS':
        addressType = 'ADDRESS_TYPE_BIZ'
        break
      case 'SALES_ORDER_TYPE_ONLINE_GROCERY':
        addressType = 'ADDRESS_TYPE_PRS'
        break
      default:
        break
    }
  }
  return addressType
}

function normalizeCustomerProfileFromResponse(response, customerId) {
  const profile = response?.data
  const rawAddresses = profile?.addresses
  const addresses = Array.isArray(rawAddresses)
    ? rawAddresses
    : rawAddresses
      ? [rawAddresses]
      : []
  const customerProfileSummary = profile
    ? {
        id: profile.id != null ? profile.id : customerId,
        name: profile.name,
        phone: profile.phone,
        email: profile.email,
      }
    : null
  return { addresses, customerProfileSummary }
}

const filterAddressByOrderType = ({ salesOrder, deliveryOrder }) => {
  const clonedOrder = _.cloneDeep(deliveryOrder)
  if (clonedOrder.customer && clonedOrder.customer.addresses) {
    const addresses = clonedOrder.customer.addresses
    const addressType = getAddressTypeFromSalesOrder(salesOrder)
    if (addresses.length === 0 || addressType === '') {
      return clonedOrder
    }
    const filteredAddress = addresses.filter(
      (address) => address.addressType === addressType
    )
    clonedOrder.customer.addresses = filteredAddress
  }
  return clonedOrder
}

function getTitleBasedOnOrderStatusAndType(orderStatus, orderType) {
  let title = getMessage('order.details.location.delivery.notSpecified.title')
  if (orderStatus === 'DELIVERY_ORDER_STATUS_COMPLETED') {
    if (orderType === 'SALES_ORDER_TYPE_ONLINE_GROCERY') {
      title = getMessage('order.details.location.delivery.completed.title')
    } else if (orderType === 'SALES_ORDER_TYPE_BUSINESS_TO_BUSINESS') {
      title = getMessage('order.details.location.b2b.completed.title')
    } else if (orderType === 'SALES_ORDER_TYPE_SCAN_AND_GO') {
      title = getMessage('order.details.location.offline.completed.title')
    } else if (orderType === 'SALES_ORDER_TYPE_RB_PREORDER') {
      title = getMessage('order.details.location.rbPreorder')
    }
  } else {
    if (orderType === 'SALES_ORDER_TYPE_ONLINE_GROCERY') {
      title = getMessage('order.details.location.delivery.pending.title')
    } else if (orderType === 'SALES_ORDER_TYPE_BUSINESS_TO_BUSINESS') {
      title = getMessage('order.details.location.b2b.pending.title')
    } else if (orderType === 'SALES_ORDER_TYPE_SCAN_AND_GO') {
      title = getMessage('order.details.location.offline.pending.title')
    } else if (orderType === 'SALES_ORDER_TYPE_RB_PREORDER') {
      title = getMessage('order.details.location.rbPreorder')
    }
  }
  return title
}

function getStartTimeAndEndTimeFromInterval(interval) {
  if (
    !interval ||
    interval.split('/').length !== 2 ||
    interval.split('T').length !== 3
  ) {
    return null
  }
  const startTime = interval.split('/')[0].split('T')[1].substring(0, 8)

  const endTime = interval.split('/')[1].split('T')[1].substring(0, 8)
  return { startTime, endTime }
}

function getAmendmentItemsFromPacklists(oldPacklists) {
  return oldPacklists.map((packlist) => {
    // sort orderAmendments by id, so we know which is the first amendment and which is the second amendment
    const sortedAmendmentItems = _.sortBy(packlist.orderAmendments, ['id']).map(
      (x) => x.id
    )
    const items = packlist.items.map((item) => {
      // check if item is a sub item because orderamendmentId is in originalItem, not item
      const amendmentItemIndex = item.originalItem
        ? sortedAmendmentItems.findIndex(
            (x) => x === item.originalItem.orderAmendmentId
          )
        : sortedAmendmentItems.findIndex((x) => x === item.orderAmendmentId)
      return {
        ...item,
        amendmentItemIndex,
      }
    })
    return {
      ...packlist,
      items: items,
    }
  })
}

function getAmendmentItemsFromDeliveryOrder(deliveryOrder) {
  // sort orderAmendments by id, so we know which is the first amendment and which is the second amendment
  const sortedAmendmentItems = _.sortBy(deliveryOrder.orderAmendments, ['id'])
  const amendments = sortedAmendmentItems.map((amendment) => {
    return amendment.orderAmendmentDetail.orderItems.flatMap(
      (x) => x.orderItemId
    )
  })
  const itemsWithAmendments = deliveryOrder.items.map((item) => {
    return {
      ...item,
      amendmentItemIndex: amendments.findIndex((x) => x.includes(item.item.id)),
    }
  })
  return {
    ...deliveryOrder,
    items: itemsWithAmendments,
  }
}

function isOperationPage(url) {
  return url.startsWith('/operations')
}

function isOrderPendingOrCancelled(status) {
  return (
    status === 'DELIVERY_ORDER_STATUS_PENDING' ||
    status === 'DELIVERY_ORDER_STATUS_CANCELLED' ||
    status === 'DELIVERY_ORDER_STATUS_PROCESSING'
  )
}

// null => return "-"
// 0    => return 0
// num  => return num
function getQuantity(quantity, decimalPlace) {
  if (quantity === null) {
    return '-'
  }
  return Number(quantity).toFixed(decimalPlace)
}

// in SaleOrder response, soldByWeight is 0 (false) or 1(true). In DeliveryOrder response, soldByWeight is true or false.
function getActualSoldByWeightValue(soldByWeight, unitOfMeasurement = '') {
  const isFreshItem = unitOfMeasurement?.toLowerCase() === 'kgm'
  if (typeof soldByWeight === 'number') {
    return soldByWeight === 1 && isFreshItem
  }
  return soldByWeight && isFreshItem
}

function isBeforePackedOrCancelled(status) {
  const statuses = [
    'DELIVERY_ORDER_STATUS_PENDING',
    'DELIVERY_ORDER_STATUS_PROCESSING',
    'DELIVERY_ORDER_STATUS_PACKING',
    'DELIVERY_ORDER_STATUS_CANCELLED',
  ]
  return statuses.includes(status)
}

const mapSalesOrderOfferToDiscount = (offer) => ({
  type: 'DISCOUNT_TYPE_OFFER',
  subType: offer.discountType,
  description: offer.description || '',
  code: offer.promoCode || offer.appliedPromoCode || '',
  offer: { offerId: String(offer.id) },
  discount: offer.discount,
})

export function getDeliveryOrderOffers(deliveryOrder) {
  const sfxgcdOfferIds = new Set(
    (deliveryOrder.salesOrderOrders ?? [])
      .flatMap((order) => order.offers ?? [])
      .filter((offer) => offer.offerType === 'SFXGCD')
      .map((offer) => String(offer.id))
  )

  const discountOffers = deliveryOrder.discounts.filter(
    (item) =>
      item.type === 'DISCOUNT_TYPE_OFFER' &&
      item.offer?.offerType !== 'SFXGCD' &&
      !sfxgcdOfferIds.has(String(item.offer?.offerId))
  )

  const orders = deliveryOrder.salesOrderOrders
  if (!orders?.length) {
    return discountOffers
  }

  const matchedOrder =
    orders.find(
      (order) => order.referenceNumber === deliveryOrder.referenceNumber
    ) ?? orders[0]

  const orderOffers = (matchedOrder?.offers ?? []).filter(
    (offer) => offer.offerType !== 'SFXGCD'
  )

  if (orderOffers.length > 0) {
    return orderOffers.map(mapSalesOrderOfferToDiscount)
  }

  return discountOffers
}

export async function fetchOfferDetail(id) {
  const url = `/offer-service/offer/${id}`
  const api = new API({ url })
  const res = await api.get()

  if (res.code !== 200) {
    throw new Error('Failed to fetch offer details: ' + res.message)
  }

  return res?.data?.offers?.description
}

export {
  filterAddressByOrderType,
  getActualSoldByWeightValue,
  getAmendmentItemsFromDeliveryOrder,
  getAmendmentItemsFromPacklists,
  getQuantity,
  getStartTimeAndEndTimeFromInterval,
  getTitleBasedOnOrderStatusAndType,
  isBeforePackedOrCancelled,
  isOperationPage,
  isOrderPendingOrCancelled,
  normalizeCustomerProfileFromResponse,
}
